import React from 'react';
import { Button, Col, Divider, Form, Input, Row, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { FormInstance } from 'antd/lib/form';
// Types
import {
  ExtTalentContactRole,
  ExtTalentContactStatus,
  ExtTalentContactType,
} from 'api/graphql-global-types';
import EnumSelect from 'components/common/EnumSelect/EnumSelect';
// Helpers
import { ExternalTalentFormValues } from 'helpers/externalTalents';
// Components
import SectionHeader from '../SectionHeader/SectionHeader';

const { Text } = Typography;

type ContactsSectionProps = {
  form: FormInstance<ExternalTalentFormValues>;
};

const ContactsSection = ({ form }: ContactsSectionProps): JSX.Element => {
  const updateContactField = (
    fieldIndex: number,
    nextValues: Record<string, unknown>
  ) => {
    const contacts = [...(form.getFieldValue('contacts') || [])];

    contacts[fieldIndex] = {
      ...contacts[fieldIndex],
      ...nextValues,
    };

    form.setFieldsValue({ contacts });
  };

  return (
    <>
      <Divider />

      <SectionHeader
        actionIcon={<PlusOutlined />}
        actionLabel="Add contact"
        onAction={() =>
          form.setFieldsValue({
            contacts: [
              ...(form.getFieldValue('contacts') || []),
              {
                agentName: '',
                role: ExtTalentContactRole.personal,
                type: ExtTalentContactType.email,
                value: '',
              },
            ],
          })
        }
        title="Contacts"
      />

      <Text type="secondary">
        Existing contact value, type, and role are read-only because the current
        API only updates contact status and agent name.
      </Text>

      <Form.List name="contacts">
        {(fields, { remove }) => (
          <>
            {fields.map((field) => {
              const contactId = form.getFieldValue([
                'contacts',
                field.name,
                'id',
              ]);
              const isExistingContact = Boolean(contactId);
              const contactType = form.getFieldValue([
                'contacts',
                field.name,
                'type',
              ]);
              const contactRole = form.getFieldValue([
                'contacts',
                field.name,
                'role',
              ]);
              const isAgentContact = contactRole === ExtTalentContactRole.agent;

              return (
                <div key={field.key}>
                  <Divider />

                  <Row gutter={16}>
                    <Col span={5}>
                      <Form.Item label="Role" name={[field.name, 'role']}>
                        <EnumSelect
                          allowClear
                          disabled={isExistingContact}
                          onChange={(value) => {
                            updateContactField(field.name, {
                              agentName:
                                value === ExtTalentContactRole.agent
                                  ? ''
                                  : undefined,
                              role: value,
                            });
                          }}
                          options={Object.values(ExtTalentContactRole)}
                        />
                      </Form.Item>
                    </Col>

                    <Col span={5}>
                      <Form.Item
                        label="Type"
                        name={[field.name, 'type']}
                        rules={[
                          {
                            message: 'Select contact type',
                            required: true,
                          },
                        ]}
                      >
                        <EnumSelect
                          allowClear
                          disabled={isExistingContact}
                          onChange={(value) => {
                            updateContactField(field.name, {
                              type: value,
                              value: '',
                            });
                          }}
                          options={Object.values(ExtTalentContactType)}
                        />
                      </Form.Item>
                    </Col>

                    <Col span={10}>
                      <Form.Item
                        dependencies={[['contacts', field.name, 'type']]}
                        label="Value"
                        name={[field.name, 'value']}
                        rules={[
                          {
                            message: 'Enter contact value',
                            required: true,
                          },
                          {
                            validator(_, value) {
                              if (
                                contactType !== ExtTalentContactType.email ||
                                !value
                              ) {
                                return Promise.resolve();
                              }

                              const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

                              return emailPattern.test(value)
                                ? Promise.resolve()
                                : Promise.reject(
                                    new Error('Please enter a valid email')
                                  );
                            },
                          },
                        ]}
                      >
                        <Input disabled={isExistingContact} />
                      </Form.Item>
                    </Col>

                    <Col span={4}>
                      {isExistingContact ? (
                        <Form.Item label="Status" name={[field.name, 'status']}>
                          <EnumSelect
                            options={Object.values(ExtTalentContactStatus)}
                          />
                        </Form.Item>
                      ) : (
                        <Form.Item label=" " style={{ marginBottom: 0 }}>
                          <Button
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => remove(field.name)}
                          />
                        </Form.Item>
                      )}
                    </Col>
                  </Row>

                  {isAgentContact && (
                    <Row gutter={16}>
                      <Col span={8}>
                        <Form.Item
                          label="Agent name"
                          name={[field.name, 'agentName']}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                    </Row>
                  )}
                </div>
              );
            })}
          </>
        )}
      </Form.List>
    </>
  );
};

export default ContactsSection;
